import { apiFetch, apiJson } from '@/lib/apiClient';

/**
 * The push wire contract (PLAN slice 13b, seams S1–S4), consumed through
 * raad-api only — the browser never talks to Traccar or a push service directly,
 * and the VAPID public key is FETCHED AT RUNTIME (seam S1), never baked into the
 * bundle, so rotating it needs no rebuild.
 *
 * These types are declared locally rather than import-typed from `api/` (the way
 * the alerts wire is): the api push module is built in parallel on this same
 * branch, so coupling `tsc` to a file that may not exist yet would block this
 * half. The one seam `tsc` COULD enforce is the SW payload (S3), and that is
 * pinned instead by `sw.test.ts` parsing the literal shape. The shapes below are
 * the PLAN's contract verbatim; the backend agent builds to the same PLAN.
 */

/** GET /api/push/vapid-public-key. When VAPID is unconfigured (the real state on
 *  day one — the private key lands in `.env` by Mahad's hand AFTER merge), the
 *  endpoint reports it unconfigured and the feature does not exist for the UI
 *  (AC-12). We treat ANY of: non-2xx, `{ configured: false }`, or a missing/empty
 *  key as unconfigured — the UI must be ABSENT, never a control that cannot work. */
export interface VapidKeyResponse {
  configured: boolean;
  publicKey?: string | null;
}

/** Resolve the VAPID public key, or `null` when the feature is unconfigured or
 *  unavailable. `null` is the single "the push feature does not exist here" signal
 *  the UI keys AC-12 off. Never throws — a network failure is treated as
 *  unavailable, because a thrown query would leave the control in an error state
 *  rather than absent. */
export async function fetchVapidPublicKey(signal?: AbortSignal): Promise<string | null> {
  const res = await apiFetch('/api/push/vapid-public-key', { signal });
  if (!res.ok) return null; // 404 / 501 / 503 — unconfigured or disabled server-side
  const text = await res.text();
  if (!text) return null;
  let body: VapidKeyResponse;
  try {
    body = JSON.parse(text) as VapidKeyResponse;
  } catch {
    return null;
  }
  if (body.configured === false) return null;
  const key = body.publicKey;
  return typeof key === 'string' && key.length > 0 ? key : null;
}

/** The subscribe payload — the browser's `PushSubscription`, flattened to the
 *  shape raad-api stores. Endpoint is per-DEVICE and UNIQUE (PLAN schema). */
export interface SubscribeInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** POST /api/push/subscriptions. Tenant-scoped and zod-validated server-side.
 *  SEAM S1/S3 note: the path is `subscriptions` (plural), pinned in the slice PLAN.
 *  An earlier draft of this client called `/push/subscribe`; the backend built to
 *  the PLAN, so the client moved. One side changes, never both. */
export async function subscribePush(input: SubscribeInput): Promise<void> {
  await apiJson<{ ok: true }>('/api/push/subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: input.endpoint,
      keys: input.keys,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    }),
  });
}

/** DELETE /api/push/subscriptions. Removes this device's subscription server-side.
 *  The dispatch record of anything already SENT survives (PLAN: endpoint frozen on
 *  `push_dispatches`), so unsubscribing never erases history. */
export async function unsubscribePush(endpoint: string): Promise<void> {
  await apiJson<{ deleted: number }>('/api/push/subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}

/**
 * Read this browser's service-worker registration, or `null` if the platform has
 * no service worker (jsdom, an unsupported browser). Never throws — a rejected
 * `ready` is treated as "no registration", so callers stay best-effort.
 */
async function readRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/** The `PushSubscription` this browser currently holds, or `null`. Never throws. */
async function readSubscription(): Promise<PushSubscription | null> {
  const reg = await readRegistration();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Re-POST this browser's EXISTING push subscription so the server re-stamps its
 * ownership (`push_subscriptions.user_id` + `tenant_id`) to the CURRENT session's
 * user and tenant.
 *
 * Called on every successful sign-in and whenever the signed-in principal changes
 * (66j reviewer seam): the subscription row is only stamped when the browser POSTs,
 * so a browser where user A enabled push keeps A's ownership — and thus A's tenant's
 * pushes filtered by A's per-type choices — until someone re-POSTs. If user B then
 * signs in on that browser, this re-stamps the row to B before the next event fires.
 *
 * SILENT and BEST-EFFORT by contract: it only ever re-POSTs a subscription the
 * browser ALREADY holds with permission ALREADY granted, so it never prompts and
 * never blocks sign-in; any failure is swallowed. It does NOT create a subscription
 * — enabling push is still the user's explicit action (`usePushNotifications`).
 */
export async function restampPushSubscription(): Promise<void> {
  try {
    // No point re-stamping a subscription the browser can no longer send to:
    // permission must be granted (revoking it invalidates the endpoint anyway).
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const sub = await readSubscription();
    if (!sub) return; // this browser never enabled push, or it was turned off
    const json = sub.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) return;
    await subscribePush({ endpoint, keys: { p256dh, auth } });
  } catch {
    /* best-effort: a failed re-stamp must never surface on the sign-in path */
  }
}

/**
 * Remove this browser's subscription SERVER-SIDE on sign-out, so the departing
 * user's tenant stops sending pushes to a device the next (possibly different)
 * user will hold. The DELETE carries the current session's bearer/cookie, so the
 * caller MUST invoke this BEFORE clearing the session.
 *
 * The browser-side `PushSubscription` is deliberately KEPT (we do NOT call
 * `sub.unsubscribe()`): sign-out is not the same intent as the user turning
 * notifications off (`usePushNotifications.disable`, which does unsubscribe
 * locally). Keeping it lets the next sign-in re-stamp via `restampPushSubscription`
 * with no fresh permission prompt.
 *
 * BEST-EFFORT and BOUNDED: sign-out must never hang or fail because of this, so the
 * server call is raced against `timeoutMs` and every error is swallowed. A DELETE
 * that hangs or rejects resolves this function within the bound and sign-out
 * proceeds.
 */
export async function removePushSubscriptionOnSignOut(timeoutMs = 2000): Promise<void> {
  // The WHOLE body is raced against the bound, not only the DELETE: reading the
  // subscription awaits `navigator.serviceWorker.ready`, which never settles when a
  // worker is supported but never activates — that must not hang sign-out either.
  const work = (async () => {
    const sub = await readSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    if (!endpoint) return;
    // Keep the local PushSubscription (see doc above) — only the server row goes.
    await unsubscribePush(endpoint);
  })().catch(() => {
    /* swallow: server-side removal is best-effort */
  });
  await Promise.race([
    work,
    new Promise<void>((resolve) => {
      setTimeout(resolve, timeoutMs);
    }),
  ]);
}

/**
 * Convert a base64url VAPID public key to the `Uint8Array` `pushManager.subscribe`
 * requires for `applicationServerKey`. This is the standard web-push decode; the
 * key itself is fetched at runtime (S1), so nothing here is build-time secret.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  // Build on an explicit ArrayBuffer so the result is a `BufferSource`
  // (`applicationServerKey` rejects the wider `ArrayBufferLike`-backed view).
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}
